/**
 * frontend/src/utils/captureError.js
 *
 * The single place the frontend reports a problem from. Everything that
 * detects an error — the render boundary, window.onerror, unhandled promise
 * rejections, failed API calls — comes through here, so there is one thing to
 * change when the System Support storage lands.
 *
 * Three rules it must never break:
 *
 *   1. It cannot throw. An error in the error reporter is the worst kind.
 *   2. It cannot report itself. A failing POST to the capture endpoint must
 *      not trigger another capture, or one network blip becomes a loop.
 *   3. It cannot send anything sensitive. Redaction happens here, before the
 *      value leaves the browser, not at display time.
 */

/** Values that must never leave the browser, matched on the key name. */
const SECRET_KEY = /pass|token|secret|auth|api[-_]?key|credential|cookie|session|otp|pin/i;
const REDACTED = '[redacted]';

/**
 * Depth-limited AND cycle-aware.
 *
 * Depth alone is not enough: a circular object truncated at depth 4 is still
 * circular, so JSON.stringify later throws and the report is silently lost.
 * `seen` breaks the cycle at the point it closes.
 */
export function redact(value, depth = 0, seenObjs = new WeakSet()) {
  if (value == null) return value;
  if (typeof value !== 'object') return value;
  // Past the limit, return a MARKER, not the value. Returning the value meant
  // the whole remaining subtree passed through unredacted — so a secret nested
  // six levels deep was sent in clear. A depth limit that returns the thing it
  // is limiting is not a limit.
  if (depth > 4) return '[truncated]';
  if (seenObjs.has(value)) return '[circular]';
  seenObjs.add(value);
  if (Array.isArray(value)) return value.slice(0, 20).map(v => redact(v, depth + 1, seenObjs));
  const out = {};
  for (const [k, v] of Object.entries(value).slice(0, 40)) {
    out[k] = SECRET_KEY.test(k) ? REDACTED : redact(v, depth + 1, seenObjs);
  }
  return out;
}

/** Errors seen in this page view, so the same one is not sent fifty times. */
const seen = new Map();
const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 3;

/** Same message from the same place is the same problem. */
const fingerprint = (payload) =>
  [payload.kind, payload.message, payload.route, (payload.stack || '').split('\n')[1] || '']
    .join('|').slice(0, 300);

let reporting = false;   // rule 2: never report a failure of the reporter

export function captureError(input = {}) {
  try {
    const err = input.error;
    const payload = {
      kind: input.kind || 'unknown',
      message: (err?.message || input.message || String(err || 'Unknown error')).slice(0, 500),
      name: err?.name || input.name || null,
      stack: (err?.stack || input.stack || '').slice(0, 8000),
      component_stack: (input.componentStack || '').slice(0, 4000),
      route: input.route || window.location?.pathname || null,
      reference: input.reference || null,
      boundary: input.boundary || null,
      api: input.api || null,
      user_agent: navigator?.userAgent?.slice(0, 300) || null,
      occurred_at: new Date().toISOString(),
      context: redact(input.context || {}),
    };

    const fp = fingerprint(payload);
    const now = Date.now();
    const entry = seen.get(fp);
    if (entry && now - entry.first < WINDOW_MS) {
      entry.count += 1;
      // Past the limit, count it locally and stop sending. The server does its
      // own grouping; this just stops a tight loop flooding the network.
      if (entry.count > MAX_PER_WINDOW) return;
      payload.repeat = entry.count;
    } else {
      seen.set(fp, { first: now, count: 1 });
    }

    // Always leave a trace in the console. Until the storage endpoint exists
    // this IS the report, and afterwards it stays useful during development.
    // eslint-disable-next-line no-console
    console.error(`[capture:${payload.kind}]`, payload.message, payload);

    send(payload);
  } catch {
    // Rule 1. Swallow deliberately — there is nowhere safe to report this.
  }
}

/**
 * Posts the payload. Deliberately not using the shared api client: that client
 * has an interceptor which will itself call captureError on failure, and the
 * two would feed each other.
 */
function send(payload) {
  if (reporting) return;
  const url = '/api/system-support/capture';
  try {
    reporting = true;
    // Last line of defence: if the payload still cannot serialise, the console
    // line above has already recorded it, so drop the POST rather than throw.
    let body;
    try { body = JSON.stringify(payload); }
    catch { reporting = false; return; }
    // keepalive lets the report survive the page being navigated away from,
    // which is common when the error is what made the person leave.
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => { /* the endpoint may not exist yet; the console has it */ })
      .finally(() => { reporting = false; });
  } catch {
    reporting = false;
  }
}

/**
 * Catches what the React boundary cannot: errors thrown outside the render
 * tree, and promise rejections nobody handled. Called once, from index.js.
 */
export function installGlobalHandlers() {
  if (typeof window === 'undefined' || window.__tmsCaptureInstalled) return;
  window.__tmsCaptureInstalled = true;

  window.addEventListener('error', (e) => {
    // Ignore resource load failures (a broken <img>), which fire the same
    // event but carry no error object and are not application faults.
    if (!e.error) return;
    captureError({ kind: 'window_error', error: e.error,
      context: { source: e.filename, line: e.lineno, column: e.colno } });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    captureError({
      kind: 'unhandled_rejection',
      error: reason instanceof Error ? reason : undefined,
      message: reason instanceof Error ? undefined : String(reason),
    });
  });
}
